import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import { SaccoRebate } from "@/@types/sacco.models.ts";

/**
 * Service class for managing Sacco rebates.
 * Extends the BaseService and provides methods to interact with the 'sacco_rebates' table in the database.
 */
export class SaccoRebatesService extends BaseService {

    /**
     * Creates a new Sacco rebate record in the database.
     *
     * @param {string} userId - The unique identifier of the user associated with the rebate.
     * @param {string} membershipId - The unique identifier of the membership associated with the rebate.
     * @param {SaccoRebateCreateRequest} request - An object containing the rebate details to be created.
     * @return {Promise<SaccoRebate>} A promise that resolves to the created Sacco rebate record.
     */
    public async create(userId: string, membershipId: string, request: SaccoRebateCreateRequest): Promise<SaccoRebate> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_rebates')
                .insert({ ...payload, membership_id: membershipId, user_id: userId })
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error creating Sacco rebate", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates a Sacco rebate record in the database with the provided data.
     *
     * @param {SaccoRebateUpdateRequest} request - An object containing the updated data for the Sacco rebate.
     *                                             It must include the ID of the record to update.
     * @return {Promise<SaccoRebate>} A promise that resolves to the updated Sacco rebate record.
     */
    async update(request: SaccoRebateUpdateRequest): Promise<SaccoRebate> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_rebates')
                .update(payload)
                .eq('id', request.id)
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error updating Sacco rebate", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches all Sacco rebates for a specific user and membership.
     *
     * @param {string} userId The unique identifier of the user.
     * @param {string} membershipId The unique identifier of the membership.
     * @return {Promise<SaccoRebate[]>} A promise that resolves to an array of Sacco rebate objects.
     */
    public async fetchAll(userId: string, membershipId: string): Promise<SaccoRebate[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_rebates')
                .select('*')
                .eq('membership_id', membershipId)
                .eq('user_id', userId)
                .order('year', { ascending: false });
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error fetching Sacco rebates", error);
            await this.handleError(error);
        }
    }

    /**
     * Deletes a record from the 'sacco_rebates' table based on the provided id.
     *
     * @param {string} id - The unique identifier of the record to be deleted.
     * @return {Promise<void>} A promise that resolves when the deletion is complete.
     */
    public async delete(id: string): Promise<void> {
        try {
            const { error } = await supabase.from('sacco_rebates').delete().eq('id', id);
            if (error) throw error;
        } catch (error) {
            console.error("Error deleting Sacco rebate", error);
            await this.handleError(error);
        }
    }
}

export default new SaccoRebatesService();
